/**
 * AiStudioOverview.js
 * Landing pane behind the "AI Studio" sidebar group: one card per tool so the
 * scope of each one is readable before anybody opens its settings.
 *
 * Artwork: a tool either names its own file via `image`, or one is expected at
 * `/img/aistudio/<id>.png`. Assets are cut-outs on a transparent background,
 * roughly square — the slot gives them the card's full height and lets them
 * float. Until a file exists the card falls back to its icon, so a missing
 * asset never shows a broken image.
 *
 * Colour: every card uses the same intenseblue theme for wash, kicker, glow
 * and the icon tile, so the set reads as one family.
 */

import React, { useState } from 'react';
import './AiStudioOverview.css';

const AI_STUDIO_TOOLS = [
  {
    id: 'research',
    tab: 'research',
    kicker: 'Find the right Leads',
    name: 'Clean & Enrich Leads',
    description:
      'Crawls website, SEO and LinkedIn for hard facts, scores every contact from 1 to 10 and sorts out the ones that never fit.',
    icon: 'fa-magnifying-glass-chart',
    image: '/img/research_hands_2.webp',
    badge: 'Tools',
  },
  {
    id: 'imageLab',
    tab: 'salesStudio',
    kicker: 'Send Value, not just Words',
    name: 'Sales Bots',
    description:
      'Attach real value to every cold email: website redesigns, on-brand posts and ads, AI images. Rendered fresh for every single contact.',
    icon: 'fa-wand-magic-sparkles',
    image: '/img/image_create_4.webp',
    badge: 'Tools',
  },
  {
    id: 'outreachFlows',
    tab: 'leadSettings',
    kicker: 'Say Hello on different Platforms',
    name: 'Flows',
    description:
      'Build the sequence once: touchpoints, waiting times and stop rules then run per lead and pause the second someone replies.',
    icon: 'fa-diagram-project',
    image: '/img/walkline_4.webp',
    badge: 'Automation',
  },
  {
    id: 'outreachAssistant',
    tab: 'outreachJobs',
    kicker: 'Daily Scheduled and Prepared For You',
    name: 'Background Agents',
    description:
      'Set a volume per day. The agent picks the leads, writes each mail and either sends it or leaves you a prepared draft.',
    icon: 'fa-paper-plane',
    image: '/img/auto_outreach_1.webp',
    badge: 'Automation',
  },
];

const AiStudioCard = ({ tool, onOpen, theme, badge = 'AI' }) => {
  const [artFailed, setArtFailed] = useState(false);
  const cardBadge = tool.badge || badge;
  const tone = !theme && cardBadge === 'Tools' ? 'tools' : theme;

  return (
    <button
      className={`aiStudioCard${tone ? ` aiStudioCard--${tone}` : ''}`}
      onClick={() => onOpen(tool.tab)}
    >
      <div className="aiStudioCardArt">
        {artFailed ? (
          <i className={`fa ${tool.icon} aiStudioCardArtFallback`} />
        ) : (
          <img
            src={tool.image || `/img/aistudio/${tool.id}.png`}
            alt=""
            loading="lazy"
            onError={() => setArtFailed(true)}
          />
        )}
      </div>

      <span className="aiStudioCardBadge" aria-hidden="true">{cardBadge}</span>

      <div className="aiStudioCardInfo">
        <span className="aiStudioCardKicker">{tool.kicker}</span>
        <span className="aiStudioCardName">
          {tool.name}
          <i className="fa fa-arrow-right aiStudioCardArrow" />
        </span>
        <span className="aiStudioCardDesc">{tool.description}</span>
      </div>
    </button>
  );
};

export const StudioOverview = ({ tools, onOpenTab, theme, badge = 'AI' }) => (
  <div className="aiStudioOverview">
    <div className="aiStudioGrid">
      {tools.map((tool) => (
        <AiStudioCard
          key={tool.id}
          tool={tool}
          onOpen={onOpenTab}
          theme={theme}
          badge={badge}
        />
      ))}
    </div>
  </div>
);

const AiStudioOverview = ({ onOpenTab, hasOutreachJobs = false }) => {
  const tools = AI_STUDIO_TOOLS.filter(
    (tool) => tool.tab !== 'outreachJobs' || hasOutreachJobs
  );

  return <StudioOverview tools={tools} onOpenTab={onOpenTab} />;
};

export default AiStudioOverview;
